import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Badge } from '../../components/Badge';
import { Button } from '../../components/Button';
import { EmptyState } from '../../components/EmptyState';
import { ErrorNotice } from '../../components/ErrorNotice';
import type { IconName } from '../../components/Icon';
import { LoadingIndicator } from '../../components/Spinner';
import { Timeline, TimelineEntry, type TimelineKind } from '../../components/Timeline';
import { useTimelineOpen } from '../../components/useTimelineOpen';
import { formatDateTime } from '../../i18n';
import type { HistoryChange } from './api';
import { useTicketHistory } from './queries';

interface HistoryPanelProps {
  ticketId: string;
  active: boolean;
}

/**
 * Der Verlauf eines Tickets -- F1-33, K-15, K-16.
 *
 * Die Eintraege stammen aus `audit_log` und nicht aus einer zweiten Quelle.
 * Genau darum ist dieser Reiter mehr als Bequemlichkeit: Was hier steht, ist
 * dasselbe, was in einer Auskunft nach Art. 15 DSGVO herauskommt. Waere es
 * eine eigene Sammlung, koennten beide auseinanderlaufen -- und die Auskunft
 * waere dann nachweislich unvollstaendig.
 *
 * **Dasselbe Akkordeon wie der Schriftwechsel.** Beide Reiter beantworten
 * dieselbe Frage -- was ist wann geschehen -- und sahen bisher verschieden
 * aus. Wer den einen bedienen kann, soll den anderen bedienen koennen.
 *
 * **Die Farbe folgt der Handlung, nicht dem Objekt.** Was geloescht oder
 * anonymisiert wurde, faellt auf; was jemand nur eingesehen hat, traegt eine
 * eigene Farbe. Das ist die Frage, mit der jemand dieses Protokoll oeffnet:
 * nicht "was wurde angefasst", sondern "ist etwas Endgueltiges passiert".
 *
 * Geladen wird erst, wenn der Reiter offen ist: Der Verlauf eines lange
 * laufenden Tickets ist der groesste Einzelabruf der Seite, und die meisten
 * Aufrufe brauchen ihn nicht.
 */
export function HistoryPanel({ ticketId, active }: HistoryPanelProps) {
  const { t, i18n } = useTranslation();
  const query = useTicketHistory(ticketId, active);

  const entries = useMemo(() => query.data ?? [], [query.data]);
  const ids = useMemo(() => entries.map((entry) => entry.id), [entries]);
  const openByDefault = useMemo(() => (ids[0] ? [ids[0]] : []), [ids]);
  const { isOpen, toggle, toggleAll, everythingOpen } = useTimelineOpen(ids, openByDefault);

  if (!active) return null;
  if (query.isPending) return <LoadingIndicator />;
  if (query.isError) return <ErrorNotice error={query.error} />;

  if (entries.length === 0) {
    return <EmptyState icon="clock" title={t('history.empty')} hint={t('history.emptyHint')} />;
  }

  return (
    <div className="stack">
      <div className="timeline-bar">
        <span className="shell__spacer" />
        <Button variant="ghost" onClick={toggleAll}>
          {everythingOpen ? t('articles.collapseAll') : t('articles.expandAll')}
        </Button>
      </div>

      <Timeline>
        {entries.map((entry) => {
          const changes = entry.changes ?? [];

          return (
            <TimelineEntry
              key={entry.id}
              kind={kindOf(entry.action)}
              icon={iconOf(entry.action)}
              toggleLabel={t('history.toggleEntry')}
              open={isOpen(entry.id)}
              onToggle={() => {
                toggle(entry.id);
              }}
              // Ohne Feldaenderungen gibt es nichts aufzuklappen -- ein
              // "angelegt" sagt in seiner Zeile alles.
              collapsible={changes.length > 0}
              badges={
                <>
                  <Badge tone={toneOf(entry.action)}>
                    {t(`history.action.${entry.action}`, { defaultValue: entry.action })}
                  </Badge>
                  {entry.actor_type !== 'user' ? (
                    <Badge tone="info">
                      {t(`history.actor.${entry.actor_type}`, { defaultValue: entry.actor_type })}
                    </Badge>
                  ) : null}
                </>
              }
              who={t(`history.object.${entry.object_type}`, { defaultValue: entry.object_type })}
              preview={summarise(changes)}
              time={formatDateTime(entry.occurred_at, i18n.language)}
            >
              {changes.length > 0 ? (
                <dl className="definition-list">
                  {changes.map((change: HistoryChange) => (
                    <div key={change.field} className="history__change">
                      <dt>{change.label}</dt>
                      <dd>
                        <span className="history__old">
                          {formatValue(change.old, t('app.none'))}
                        </span>
                        {' → '}
                        <span className="history__new">
                          {formatValue(change.new, t('app.none'))}
                        </span>
                      </dd>
                    </div>
                  ))}
                </dl>
              ) : null}
            </TimelineEntry>
          );
        })}
      </Timeline>
    </div>
  );
}

/**
 * Welche Farbe eine Handlung traegt.
 *
 * Endgueltiges faellt auf (`internal`, der warnende Ton), Einsicht in
 * geschuetzte Daten bekommt eine eigene Farbe, alles Uebrige bleibt ruhig.
 * Ein Protokoll, in dem jede Zeile schreit, sagt nichts mehr.
 */
function kindOf(action: string): TimelineKind {
  switch (action) {
    case 'delete':
    case 'anonymize':
      return 'internal';
    case 'create':
      return 'out';
    case 'read_sensitive':
    case 'export':
      return 'in';
    case 'login':
    case 'login_failed':
      return 'system';
    default:
      return 'neutral';
  }
}

function iconOf(action: string): IconName {
  switch (action) {
    case 'create':
      return 'plus';
    case 'delete':
      return 'trash';
    case 'anonymize':
      return 'privacy';
    case 'read_sensitive':
      return 'search';
    case 'export':
      return 'upload';
    case 'login':
    case 'login_failed':
      return 'security';
    case 'update':
      return 'note';
    default:
      return 'system';
  }
}

function toneOf(action: string) {
  switch (action) {
    case 'delete':
    case 'anonymize':
      return 'danger' as const;
    case 'create':
      return 'success' as const;
    default:
      return 'neutral' as const;
  }
}

/** Welche Felder sich geaendert haben -- die Zeile fuer den zugeklappten Eintrag. */
function summarise(changes: HistoryChange[]): string {
  if (changes.length === 0) return '';
  const labels = changes.map((change) => change.label);
  return labels.length > 4 ? `${labels.slice(0, 4).join(', ')} …` : labels.join(', ');
}

/**
 * Stellt einen protokollierten Wert dar.
 *
 * Werte im Protokoll sind bewusst als JSON abgelegt -- so bleibt erkennbar,
 * ob ein Feld leer war oder auf `null` gesetzt wurde. Fuer die Anzeige
 * genuegt eine knappe Form; die genaue Gestalt sieht, wer die Auskunft nach
 * Art. 15 anfordert.
 */
function formatValue(value: unknown, emptyLabel: string): string {
  if (value === null || value === undefined || value === '') return emptyLabel;
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  if (Array.isArray(value)) return value.map((item) => formatValue(item, emptyLabel)).join(', ');
  return JSON.stringify(value);
}
